import { Box, Breadcrumbs, Container, Grid, Link, Skeleton, Tab, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import { useRuuviTagLoader } from '../../loaders'
import PageHeader from '../../components/PageHeader'
import RuuviTagPageTitle from '../../components/RuuviTagPageTitle'
import { formatMacAddress } from '../../utils/string'
import LoadingState from '../../components/LoadingState'
import { TabContext, TabList } from '@mui/lab'
import { SyntheticEvent, useEffect, useRef, useState } from 'react'
import RuuviTagActiveAlertsCard from '../../components/RuuviTagActiveAlertsCard'
import RuuviTagAlertHistoryCard from '../../components/RuuviTagAlertHistoryCard'
import RuuviTagAlertRuleManagementCard from '../../components/RuuviTagAlertRuleManagementCard'

type RuuviTagAlertsTab = 'active-alerts' | 'alert-rules' | 'alert-history'

export default function RuuviTagAlerts() {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The route params.
   */
  const { id } = useParams()

  if (id === undefined) {
    throw new Error('RuuviTag ID is required.')
  }

  /**
   * The loader state.
   */
  const { ruuviTag } = useRuuviTagLoader(id)

  /**
   * The tab state.
   */
  const [tab, setTab] = useState<RuuviTagAlertsTab>('active-alerts')

  /**
   * The active alerts element.
   */
  const activeAlertsRef = useRef<HTMLElement | null>(null)

  /**
   * The alert rules element.
   */
  const alertRulesRef = useRef<HTMLElement | null>(null)

  /**
   * The alert history element.
   */
  const alertHistoryRef = useRef<HTMLElement | null>(null)

  /**
   * Handle the tab change.
   */
  function handleTabChange(_event: SyntheticEvent, tab: RuuviTagAlertsTab) {
    setTab(tab)
  }

  /**
   * Scroll into view when tab changes.
   */
  useEffect(() => {
    switch (tab) {
      case 'active-alerts':
        activeAlertsRef.current?.scrollIntoView({
          behavior: 'smooth'
        })
        break
      case 'alert-rules':
        alertRulesRef.current?.scrollIntoView({
          behavior: 'smooth'
        })
        break
      case 'alert-history':
        alertHistoryRef.current?.scrollIntoView({
          behavior: 'smooth'
        })
        break
    }
  }, [tab])

  return (
    <>
      <PageHeader
        title={t('conditionMonitoring')}
        heading={
          ruuviTag === null ? (
            <Skeleton animation="wave" variant="text" width="120px" sx={{ fontSize: '1.5rem' }} />
          ) : (
            <RuuviTagPageTitle ruuviTag={ruuviTag} />
          )
        }
        breadcrumbs={
          <Breadcrumbs>
            {ruuviTag && ruuviTag?.asset?.project?.name && (
              <Link component={NavLink} to={`/projects/${ruuviTag.asset.projectId}`}>
                {ruuviTag.asset?.project?.name}
              </Link>
            )}

            {ruuviTag && ruuviTag?.asset?.project?.name ? (
              <Link component={NavLink} to={`/projects/${ruuviTag.asset.projectId}#monitoring `}>
                {t('deviceMonitoring')}
              </Link>
            ) : ruuviTag ? (
              <Link component={NavLink} to="/ruuvi-tags">
                {t('conditionMonitoring')}
              </Link>
            ) : (
              <Skeleton animation="wave" variant="text" width="40px" height="1em" />
            )}

            {ruuviTag ? (
              <Link component={NavLink} to={`/ruuvi-tags/${ruuviTag.id}`}>
                {formatMacAddress(ruuviTag.id)}
              </Link>
            ) : (
              <Skeleton animation="wave" variant="text" width="40px" height="1em" />
            )}

            <Typography>{t('alertManagement')}</Typography>
          </Breadcrumbs>
        }
      />

      <Container maxWidth={false}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {ruuviTag === null ? (
              <LoadingState />
            ) : (
              <>
                <TabContext value={tab}>
                  <TabList onChange={handleTabChange} sx={{ mb: 6 }}>
                    <Tab label={t('activeAlerts')} value="active-alerts" />
                    <Tab label={t('alertRules')} value="alert-rules" />
                    <Tab label={t('alertHistory')} value="alert-history" />
                  </TabList>
                </TabContext>

                <Grid container spacing={6}>
                  <Grid item xs={12}>
                    <Box ref={activeAlertsRef}>
                      <RuuviTagActiveAlertsCard ruuviTag={ruuviTag} />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box ref={alertRulesRef}>
                      <RuuviTagAlertRuleManagementCard ruuviTag={ruuviTag} />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box ref={alertHistoryRef}>
                      <RuuviTagAlertHistoryCard ruuviTag={ruuviTag} />
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  )
}
